<template>
  <div class="iv-page">
    <!-- Header -->
    <div class="iv-header">
      <div class="iv-header-main">
        <div class="iv-eyebrow">Insurance verification</div>
        <h1 class="iv-title">Benefits &amp; eligibility</h1>
        <p class="iv-subtitle">
          Real-time eligibility pulled from each carrier, checked automatically before every
          appointment. Flags the coverage issues your front desk needs to act on.
        </p>
      </div>
      <!-- 6c: "Precheck settings" opens the automation + payer-mapping modal.
           (The Overview/Console layout toggle stays deferred.)
           Admin-only: this is a set-once-at-setup surface (automation, billing
           provider, PDF import, payer mapping) where a bad change silently
           breaks every check — hidden from non-admins (matches how other
           admin-only controls are hidden, e.g. Header.vue isAdminOrAbove). -->
      <button v-if="isAdminOrAbove" type="button" class="iv-settings-btn" @click="openSettings">
        <i class="fa fa-sliders" aria-hidden="true"></i>
        Precheck settings
      </button>
    </div>

    <!-- Feature-off notice (belt-and-suspenders; the route guard normally
         prevents reaching this page when the flag is off). -->
    <div v-if="!dashboard.enabled" class="iv-notice">
      <i class="fa fa-lock" aria-hidden="true"></i>
      <div>
        <div class="iv-notice-title">Insurance Verification isn't enabled for this practice.</div>
        <FeatureRequestButton
          class="btn btn-sm btn-primary mt-2"
          feature-key="INSURANCE_VERIFICATION"
          feature-label="Insurance Verification"
          source-page="Insurance Verification page"
        />
        <FeatureRequestNote feature="INSURANCE_VERIFICATION" />
      </div>
    </div>

    <template v-else>
      <!-- Stat cards -->
      <StatCards :stats="dashboard.statCards" :loading="dashboard.loading" />

      <!-- Action required -->
      <ActionRequiredPanel
        :chip-counts="dashboard.chipCounts"
        :unmapped-carriers="dashboard.unmappedCarriers"
      :excluded-carriers="dashboard.excludedCarriers"
        :data-issues-count="dashboard.dataIssuesCount"
        :active-issue="filters.issue"
        :timeframe-label="timeframeLabel"
        @select-issue="onSelectIssue"
      />

      <!--
        Carriers PAUSED because their payer rejected the practice's billing provider (2026-09-18).
        Site-level, above the table: every patient on that carrier is affected at once. Resume is
        admin-only — it restarts billed checks — the same gate as Precheck settings.
      -->
      <CarrierPauseBanner
        :pauses="dashboard.carrierPauses"
        :can-resume="isAdminOrAbove"
        :resuming-carrier-num="resumingCarrierNum"
        :timezone="timezone"
        @resume="onResumeCarrier"
      />

      <!-- Error banner -->
      <div v-if="dashboard.error" class="iv-error">
        <span>{{ dashboard.error }}</span>
        <button type="button" class="iv-error-retry" @click="loadDashboard">
          <i class="fa fa-refresh" aria-hidden="true"></i> Retry
        </button>
      </div>

      <!-- Table card -->
      <div class="iv-table-card">
        <VerificationFilters
          :active-timeframe="filters.activeTimeframe"
          :from="filters.from"
          :to="filters.to"
          :search="filters.search"
          :carrier="filters.carrier"
          :issue-label="issueLabel"
          :include-completed="filters.includeCompleted"
          @change-timeframe="onChangeTimeframe"
          @change-dates="onChangeDates"
          @change-search="onChangeSearch"
          @change-carrier="onChangeCarrier"
          @toggle-completed="onToggleCompleted"
          @search-all="runPatientSearch"
          @clear-issue="onClearIssue"
          @clear-all="onClearAll"
        />

        <StaleBanner
          :stale-count="dashboard.staleCount"
          :stale-days="staleDays"
          :flag-stale="flagStale"
        />

        <!--
          Search mode. The banner is not decoration: these rows are NOT the schedule, the
          date window does not apply to them, and most carry no appointment at all. Without
          it the table silently changes meaning.
        -->
        <div v-if="searchMode" class="iv-searchmode">
          <div class="iv-searchmode-text">
            <i class="fa fa-search" aria-hidden="true"></i>
            Showing patients matching <strong>“{{ patientSearch.q }}”</strong> — date window not applied.
            <span v-if="patientSearch.truncated" class="iv-searchmode-more">
              More patients match than are shown — narrow the search to see them.
            </span>
          </div>
          <button type="button" class="iv-searchmode-back" @click="exitPatientSearch">
            Back to schedule
          </button>
        </div>

        <div v-if="patientSearch.error" class="iv-searchmode iv-searchmode--error">
          <div class="iv-searchmode-text">{{ patientSearch.error }}</div>
          <button type="button" class="iv-searchmode-back" @click="exitPatientSearch">Back to schedule</button>
        </div>

        <VerificationTable
          :rows="visibleRows"
          :timezone="timezone"
          :loading="searchMode ? patientSearch.loading : dashboard.loading"
          :verify-state="verifyByPat"
          :automation="dashboard.automation"
          @open-row="onOpenRow"
          @verify-row="onVerifyRow"
        />

        <!--
          The client-side filter came up empty. That is exactly the moment the old dashboard
          dead-ended: the patient may well exist, just not with an appointment in this
          window. Offer the wider search rather than leaving "no results" as the last word.
        -->
        <div v-if="showSearchAllPrompt" class="iv-searchall">
          No one in this date range matches <strong>“{{ filters.search.trim() }}”</strong>.
          <button
            type="button"
            class="iv-searchall-btn"
            :disabled="patientSearch.loading"
            @click="runPatientSearch"
          >
            <i v-if="patientSearch.loading" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            Search all patients
          </button>
        </div>

        <div class="iv-table-footer">
          <span v-if="searchMode">
            Showing <strong>{{ patientSearch.rows.length }}</strong> matching patients
          </span>
          <span v-else>
            Showing <strong>{{ filteredRows.length }}</strong> of {{ dashboard.rows.length }} appointments
          </span>
          <span>{{ searchMode ? 'Patient search' : timeframeLabel }}</span>
        </div>
      </div>
    </template>

    <!-- Detail drawer (6b) — opens on row / patient-name click; closes without a
         route change (v-if overlay), refreshes on Verify Now. -->
    <VerificationDrawer
      :open="drawer.open"
      :row="drawer.row"
      :site-id="siteID"
      :timezone="timezone"
      :verify-state="verifyStateForDrawer"
      :confirm-request="confirmRequest"
      :requested-codes="ivSettings && ivSettings.procedureCodes"
      :default-network-view="(ivSettings && ivSettings.benefitsNetworkView) || 'both'"
      @close="onCloseDrawer"
      @retry="onRetryDetail"
      @verify="onDrawerVerify"
      @load-plan="onLoadPlan"
    />

    <!-- 6c: Precheck settings modal (automation + payer mapping). Optimistic-close;
         a successful save updates the store settings cache → staleDays/flagStale. -->
    <PrecheckSettingsModal
      :open="settingsOpen"
      :site-id="siteID"
      @close="settingsOpen = false"
    />
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions } from 'vuex';
import mixin from '@/mixin.js';

import StatCards from './components/StatCards.vue';
import ActionRequiredPanel from './components/ActionRequiredPanel.vue';
import CarrierPauseBanner from './components/CarrierPauseBanner.vue';
import VerificationFilters from './components/VerificationFilters.vue';
import StaleBanner from './components/StaleBanner.vue';
import VerificationTable from './components/VerificationTable.vue';
import VerificationDrawer from './components/VerificationDrawer.vue';
import PrecheckSettingsModal from './components/PrecheckSettingsModal.vue';
import FeatureRequestButton from '@/components/FeatureRequestButton/FeatureRequestButton.vue';
import FeatureRequestNote from '@/components/FeatureRequestButton/FeatureRequestNote.vue';
import {
  rangeForTimeframe,
  ymdRangeToDateTime,
  rowMatchesIssue,
  rowDataIssue,
  chipForCode,
  TIMEFRAME_LABELS,
  verifyReasonMessage,
  classifyVerifyError,
} from './components/constants.js';

// Design default window: upcoming appointments over the next 30 days.
const DEFAULT_TIMEFRAME = 'next30';

export default {
  name: 'InsuranceVerification',
  mixins: [mixin],
  components: {
    StatCards,
    ActionRequiredPanel,
    CarrierPauseBanner,
    VerificationFilters,
    StaleBanner,
    VerificationTable,
    VerificationDrawer,
    PrecheckSettingsModal,
    FeatureRequestButton,
    FeatureRequestNote,
  },

  data() {
    return {
      filters: {
        activeTimeframe: DEFAULT_TIMEFRAME,
        from: '',   // YMD (bound to the date inputs)
        to: '',     // YMD
        search: '',
        carrier: '',
        issue: null, // active Action-Required chip code
        // Also list appointments the office has already completed. OFF by default: this
        // page is the day's worklist, and completed visits are a lookup. A patient used to
        // disappear from here the moment their visit was marked Complete, which left no way
        // to verify them at all (Mint prod, PatNum 4198).
        // ⛔ Server-side — it changes the query, so it refetches rather than filtering rows.
        includeCompleted: false,
      },
      // 6c: Precheck settings modal open flag.
      settingsOpen: false,

      // The carrier whose "Resume checks" request is in flight (CarrierPauseBanner) — one at a
      // time; the banner disables that card's buttons so a double click can't send twice.
      resumingCarrierNum: null,

      // Detail drawer (6b): open flag + the row that opened it (patient identity
      // + appointment context for a drawer-initiated Verify Now).
      drawer: {
        open: false,
        row: null,
      },

      // Bumped by the TABLE's inline Verify button to arm the drawer's confirmation
      // panel. A counter rather than a boolean so clicking Verify on an already-open
      // drawer re-arms it — see the drawer's confirmRequest watcher.
      confirmRequest: 0,

      // Per-patient Verify Now state, keyed by patNum → { loading, message, type }.
      // Drives the per-row spinner/disable AND the drawer's inline message. Use
      // $set so new keys stay reactive (Vue 2).
      verifyByPat: {},
    };
  },

  computed: {
    ...mapState('insuranceVerification', {
      dashboard: (state) => state.dashboard,
      ivSettings: (state) => state.settings.value,
      patientSearch: (state) => state.search,
    }),

    // In search mode the table shows results instead of the schedule. The schedule is left
    // loaded underneath so "Back to schedule" is instant and keeps the user's date window.
    searchMode() {
      return this.patientSearch.active === true;
    },

    // The rows the table actually renders. Search results are already the exact set the
    // user asked for, so the client-side name/carrier/issue filters are NOT applied to
    // them — narrowing a deliberate search by a leftover filter is how a patient you just
    // searched for fails to appear.
    visibleRows() {
      return this.searchMode ? this.patientSearch.rows : this.filteredRows;
    },

    // Offer the escape hatch exactly when the client-side filter has failed the user:
    // they typed a name and nothing in the loaded window matches.
    showSearchAllPrompt() {
      return !this.searchMode
        && (this.filters.search || '').trim().length >= 2
        && this.filteredRows.length === 0
        && !this.dashboard.loading;
    },

    // Practice-level admin gate for the Precheck settings surface. Same
    // Admin/Super-Admin convention used for admin-only controls elsewhere
    // (Header.vue isAdminOrAbove, MyAccount.vue isAdminOrAbove,
    // Settings.vue canViewPaymentSettings). NOT the SuperAdmin-only tier and
    // NOT the hardcoded-site isAdminSite; Super Admin is included as the usual
    // never-lock-ourselves-out safety net. `user` comes from the global mixin.
    isAdminOrAbove() {
      return !!(this.user && (this.user.Role === 'Admin' || this.user.Role === 'Super Admin'));
    },

    // 6c: staleDays + flagStale now come from the site's Precheck settings
    // (fetched on load, cached in the store), replacing 6a's hardcoded 30.
    // Defaults mirror settings-defaults.js until the fetch resolves.
    staleDays() {
      const s = this.ivSettings;
      const n = s && Number(s.staleDays);
      return Number.isFinite(n) && n > 0 ? n : 30;
    },
    flagStale() {
      const s = this.ivSettings;
      // Default ON (absent → true), matching the backend resolve.
      return !s || s.flagStale !== false;
    },

    timezone() {
      return (this.allSettings
        && this.allSettings.GENERAL_TIMEZONE
        && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || '';
    },

    // Client-side filtering of the loaded rows: patient-name search, carrier
    // substring, and the active Action-Required issue chip. The date window is
    // server-side (drives the fetch); these three never refetch.
    filteredRows() {
      const rows = Array.isArray(this.dashboard.rows) ? this.dashboard.rows : [];
      const search = (this.filters.search || '').trim().toLowerCase();
      const carrier = (this.filters.carrier || '').trim().toLowerCase();
      const issue = this.filters.issue;

      return rows.filter((r) => {
        if (issue && !rowMatchesIssue(r, issue)) return false;
        if (search) {
          const name = `${(r.patient && r.patient.fName) || ''} ${(r.patient && r.patient.lName) || ''}`.toLowerCase();
          if (name.indexOf(search) === -1) return false;
        }
        if (carrier) {
          const cn = ((r.insurance && r.insurance.carrierName) || '').toLowerCase();
          if (cn.indexOf(carrier) === -1) return false;
        }
        return true;
      });
    },

    issueLabel() {
      if (!this.filters.issue) return '';
      return chipForCode(this.filters.issue).label;
    },

    timeframeLabel() {
      if (this.filters.activeTimeframe) {
        return TIMEFRAME_LABELS[this.filters.activeTimeframe] || '';
      }
      const from = this.filters.from || '…';
      const to = this.filters.to || '…';
      return `${from} → ${to}`;
    },

    // The verify state entry for the patient currently in the drawer (or null).
    verifyStateForDrawer() {
      const pat = this.drawer.row && this.drawer.row.patient && this.drawer.row.patient.patNum;
      if (pat === null || pat === undefined) return null;
      return this.verifyByPat[pat] || null;
    },
  },

  watch: {
    siteID: {
      immediate: true,
      handler(newSite) {
        if (newSite) this.init();
      },
    },
  },

  methods: {
    ...mapActions('insuranceVerification', ['fetchDashboard', 'fetchDetail', 'verifyPatient', 'resetDetail', 'fetchSettings', 'markSecondaryNotVerified', 'searchPatients', 'clearPatientSearch', 'resumeCarrier']),

    // Search every patient, not just the loaded window. This is the only way to reach a
    // patient whose appointment is finished or who has none at all.
    async runPatientSearch() {
      const q = (this.filters.search || '').trim();
      if (q.length < 2 || !this.siteID) return;
      await this.searchPatients({ siteID: this.siteID, q });
    },

    // Back to the day's worklist. The dashboard was never unloaded, so this is instant and
    // the date window / filters are exactly as they were left.
    exitPatientSearch() {
      this.clearPatientSearch();
    },

    // Seed the default date window (site TZ) then load. Also pull the site's
    // Precheck settings (cached in the store) so the StaleBanner uses the real
    // staleDays/flagStale, not the 6a default.
    init() {
      const range = rangeForTimeframe(DEFAULT_TIMEFRAME, this.timezone);
      this.filters.activeTimeframe = DEFAULT_TIMEFRAME;
      this.filters.from = range.fromDate;
      this.filters.to = range.toDate;
      this.loadDashboard();
      this.loadSettings();
    },

    loadSettings() {
      if (!this.siteID) return;
      this.fetchSettings({ siteID: this.siteID })
        .catch(() => { /* non-fatal — StaleBanner falls back to defaults */ });
    },

    openSettings() {
      // Defense in depth: never open the settings modal for a non-admin, even
      // if the (admin-only) button were bypassed. The modal guards its open
      // path too.
      if (!this.isAdminOrAbove) return;
      this.settingsOpen = true;
    },

    async loadDashboard() {
      if (!this.siteID) return;
      const { from, to } = ymdRangeToDateTime(this.filters.from, this.filters.to);
      if (!from || !to) return;
      try {
        await this.fetchDashboard({
          siteID: this.siteID,
          fromDateTime: from,
          toDateTime: to,
          includeCompleted: this.filters.includeCompleted === true,
        });
      } catch (e) {
        /* error surfaced in store state */
      }
    },

    /**
     * The carrier-pause banner's "Resume checks" (already confirmed inside the banner).
     *
     * ⛔ Admin-only, checked HERE too — the banner hides the button for non-admins, but this
     * restarts billed checks on a whole carrier, so the page refuses on its own authority as
     * well. (The backend trusts the request body — known debt shared by every IV endpoint.)
     * Refetches the dashboard afterwards: the banner card drops once the pause is gone.
     */
    async onResumeCarrier(carrierNum) {
      if (!this.isAdminOrAbove) return;
      if (!this.siteID || carrierNum === null || carrierNum === undefined) return;
      if (this.resumingCarrierNum !== null) return;
      this.resumingCarrierNum = carrierNum;
      try {
        const data = await this.resumeCarrier({
          siteID: this.siteID,
          carrierNum,
          employeeID: this.user && this.user.EmployeeID,
        });
        if (this.$toast) {
          if (data && data.resumed) this.$toast.success('Automatic checks resumed for this carrier.');
          else this.$toast.info('That carrier was no longer paused.');
        }
        await this.loadDashboard();
      } catch (err) {
        if (this.$toast) this.$toast.error((err && err.message) || 'Could not resume checks. Please try again.');
      } finally {
        this.resumingCarrierNum = null;
      }
    },

    // Unlike search/carrier/issue, this one changes the SERVER query — completed
    // appointments were never fetched, so there is nothing client-side to filter.
    onToggleCompleted(next) {
      const want = next === true;
      if (this.filters.includeCompleted === want) return;
      this.filters.includeCompleted = want;
      this.loadDashboard();
    },

    // Timeframe preset → recompute the server window and refetch.
    onChangeTimeframe(key) {
      const range = rangeForTimeframe(key, this.timezone);
      this.filters.activeTimeframe = key;
      this.filters.from = range.fromDate;
      this.filters.to = range.toDate;
      this.loadDashboard();
    },

    // Custom date-input change → drop the preset highlight, refetch.
    onChangeDates({ from, to }) {
      this.filters.activeTimeframe = null;
      this.filters.from = from || '';
      this.filters.to = to || '';
      // Only refetch once both ends are set (a half-open range would be a
      // no-op fetch anyway).
      if (this.filters.from && this.filters.to) this.loadDashboard();
    },

    // Client-side only — no refetch.
    onChangeSearch(value) {
      this.filters.search = value;
    },
    onChangeCarrier(value) {
      this.filters.carrier = value;
    },
    onSelectIssue(code) {
      this.filters.issue = code;
    },
    onClearIssue() {
      this.filters.issue = null;
    },
    onClearAll() {
      this.filters.search = '';
      this.filters.carrier = '';
      this.filters.issue = null;
      // ⛔ Deliberately does NOT reset includeCompleted. These three narrow rows already on
      // screen; includeCompleted changes what is FETCHED, so clearing it here would fire a
      // silent refetch and make rows disappear from a "clear filters" click. It is also a
      // visible segmented control, so its state is never hidden from the user.
    },

    // Open the patient detail drawer for this row and load its detail payload.
    onOpenRow(row) {
      if (!row || !row.patient) return;
      this.drawer.row = row;
      this.drawer.open = true;
      this.loadDetail(row);
    },

    /**
     * `latest:true` ignores the row's verificationId and asks for the patient's latest.
     * ⛔ Do NOT use it post-verify: `IsLatest` is per (SiteID, PatNum, PlanNum), so a
     * dual-plan patient has two and the secondary — checked last — wins, rendering the
     * secondary's benefits under the Primary tab.
     *
     * `verificationId` overrides the row entirely. Post-verify that is the id /verify just
     * returned, which is the only id guaranteed to be the check that was actually run.
     *
     * When there is no id we send the row's PRIMARY `planNum` so the backend resolves the
     * same (PatNum, PlanNum) key the dashboard already matched on — otherwise it falls back
     * to any-plan-latest, and a patient who changed carriers gets the OLD plan's benefits
     * under the NEW carrier's name. `latest:true` deliberately omits it: that path is asking
     * "whatever is newest", so constraining it to a plan would change what it means.
     */
    loadDetail(row, { latest = false, verificationId = null } = {}) {
      if (!this.siteID || !row || !row.patient) return;
      const patNum = row.patient.patNum;
      const id = verificationId
        || (latest ? null : ((row.verification && row.verification.verificationId) || null));
      const planNum = (!id && !latest && row.insurance) ? (row.insurance.planNum || null) : null;
      this.fetchDetail({ siteID: this.siteID, patNum, verificationId: id, planNum })
        .catch(() => { /* error surfaced in store detail state → drawer shows Retry */ });
    },

    onCloseDrawer() {
      this.drawer.open = false;
      // Keep the row briefly so the closing frame doesn't flash empty; clear the
      // store detail so a re-open always shows a fresh load, never stale benefits.
      this.drawer.row = null;
      this.resetDetail();
    },

    onRetryDetail() {
      if (this.drawer.row) this.loadDetail(this.drawer.row);
    },

    /**
     * The drawer switched to the Secondary tab. Load that plan's own detail into
     * the store's `secondaryDetail` slice.
     *
     * The id comes off the dashboard row (`row.secondary.verification.verificationId`)
     * — the backend has shipped it since secondary checking went in, and the /detail
     * endpoint resolves any verification id without caring which plan it belongs to,
     * so this needs no backend change.
     *
     * If the secondary has never been checked there is no id, and passing patNum
     * instead would resolve the PRIMARY's latest check — the same benefits under a
     * "Secondary" tab, which is worse than an empty state. So we fetch by id only,
     * and let the drawer show its "not verified yet" state when there isn't one.
     */
    onLoadPlan(ordinal, explicitId = null) {
      if (ordinal !== 2) return;
      const row = this.drawer.row;
      const sec = row && row.secondary;
      // An explicit id (post-verify) beats the row's, which may predate the check that
      // just ran — or be missing entirely if the refresh did not return this patient.
      const verificationId = explicitId
        || (sec && sec.verification && sec.verification.verificationId)
        || null;
      if (!this.siteID) return;
      const patNum = (row.patient && row.patient.patNum);
      if (!verificationId) {
        // Never checked → the same empty state a 404 gives, not a blank panel.
        this.markSecondaryNotVerified({ patNum });
        return;
      }
      this.fetchDetail({
        siteID: this.siteID,
        patNum: (row.patient && row.patient.patNum),
        verificationId,
        slot: 'secondary',
      }).catch(() => { /* error surfaced in store secondaryDetail state → drawer shows Retry */ });
    },

    /**
     * Re-fetch plan detail after a check. MUST be called with an already-refreshed
     * `drawer.row` — see the ordering note in runVerify.
     *
     * Fetches by the row's own verification ids rather than `{latest:true}`,
     * because "latest" is ambiguous for a dual-plan patient and resolves to the
     * secondary (see runVerify). `loadDetail(row)` without `latest` already reads
     * `row.verification.verificationId`, so a fresh row makes it exact — and it
     * still degrades to the latest-by-patNum path for a single-plan patient whose
     * row carries no id yet.
     */
    /**
     * Reload the drawer's detail after a check.
     *
     * `ids` are the verification ids /verify just returned — `{ primary, secondary }`, either
     * possibly null. They take precedence over whatever the refreshed row carries.
     *
     * ⛔ Why that matters, and why it is not just tidiness: the row rebind depends on the
     * refreshed list still CONTAINING this patient. It usually does, but not always — the
     * date window can no longer match, a concurrent edit can move the appointment, a search
     * can return a different set. When it does not, `drawer.row` keeps its PRE-verify
     * verificationId and this function silently re-fetches the OLD check, presenting a stale
     * result as the outcome of the check the user just paid for. Fetching by the returned id
     * removes that dependency entirely.
     */
    reloadPlanDetail(ordinal, ids = null) {
      if (!this.drawer.open || !this.drawer.row) return;
      const row = this.drawer.row;
      const primaryId = (ids && ids.primary) || null;
      const secondaryId = (ids && ids.secondary) || null;

      if (ordinal === 2) {
        // Scoped to the secondary — the primary's stored result did not change.
        // The scoped run's own id arrives as `primary` (it is the only check that ran).
        this.onLoadPlan(2, primaryId || secondaryId);
        return;
      }

      this.loadDetail(row, { verificationId: primaryId });

      // An UNSCOPED check re-ran the secondary too. The page doesn't track which
      // tab the drawer is showing, so refresh that slice as well rather than leave
      // a stale result sitting under the Secondary tab. Detail fetches are free —
      // no Stedi call — so this costs a round-trip, not money.
      const sec = row.secondary;
      if (!ordinal && (secondaryId || (sec && sec.insurance && sec.insurance.hasInsurance))) {
        this.onLoadPlan(2, secondaryId);
      }
    },

    /**
     * Verify from a table row.
     *
     * ⛔ This does NOT spend. It opens the drawer and arms its confirmation panel,
     * so the table button and the drawer button are the SAME path to money.
     *
     * Before this, the table's inline button called runVerify directly with no
     * confirmation of any kind, while the drawer's button always confirmed first.
     * That gap got worse when secondary-plan checking shipped: one click on a
     * dual-plan patient is TWO billed checks, and the drawer's confirm panel is the
     * only surface that says so. Routing both through one panel is also why there
     * is no second copy of the confirm markup to drift out of sync.
     */
    onVerifyRow(row) {
      if (!row || !row.patient) return;

      // ⛔ CONFIRM ONLY WHEN THE SPEND IS REDUNDANT — not on every check.
      //
      // This button used to run the check directly with no guard at all, which was a
      // real hole: one click is a billed Stedi check (TWO on a dual-plan patient). On
      // 2026-08-06 it was routed through the drawer's confirm panel to close that.
      //
      // That over-corrected, and Nilay hit it immediately on the Mint pilot: the
      // primary action of the page grew a modal, and a never-verified patient opened
      // with the confirm panel AND the "Not verified yet" panel's own Verify button
      // showing at once — the latter a literal no-op, since requestVerify() only sets
      // confirmOpen, which was already true. Two clicks, three buttons, no feedback.
      //
      // The deeper mistake was WHERE the guard sat: a speed bump AFTER the decision.
      // A dialog that fires on every click is muscle-memoried through within a day —
      // it buys friction, not safety. So the guard now fires on the one click that is
      // actually questionable: re-checking someone the system already considers
      // covered. `row.isFresh` means the nightly run would have SKIPPED this patient
      // (verified inside settings.frequencyMonths — dashboard-assemble.js's
      // computeRowFreshness, whose own comment calls it "the already-verified-recently
      // hint for the Verify button"). Paying to redo that is worth one beat.
      //
      // Everything else — never verified, or verified long enough ago that a re-check
      // is legitimate — runs immediately. The row spinner and the post-refresh status
      // update were always there; they were just being intercepted.
      //
      // Deliberately NOT surfaced here: the dual-plan "2 checks" cost. Nilay's call —
      // a per-row cost label is noise during routine work. It stays in the completion
      // toast, which already distinguishes one check from two.
      if (row.isFresh) {
        this.onOpenRow(row);
        this.confirmRequest += 1;
        return;
      }

      this.runVerify(row, null);
    },

    // Verify (Re-check / Verify now) from inside the drawer. The payload carries
    // the SCOPE the confirm panel was armed with: `{ ordinal: null }` = every plan
    // (header button, table button), `{ ordinal: 1|2 }` = that plan only.
    onDrawerVerify(payload) {
      if (!this.drawer.row) return;
      this.runVerify(this.drawer.row, (payload && payload.ordinal) || null);
    },

    /**
     * Run an on-demand verification for a row's patient, passing the
     * appointment's provider/appt context so the check can fall back to the
     * appointment provider. Per-patient spinner via verifyByPat; friendly
     * messaging for pre-flight bailouts (no_billing_provider, no_billing_npi, …) and for the
     * API-Gateway-29s-vs-Lambda-60s timeout. Never leaves an unhandled rejection.
     *
     * `ordinal` (null | 1 | 2) is the COST SCOPE — null checks every plan the
     * patient carries (two billed checks on a dual-plan patient), 1 or 2 checks
     * exactly that plan. See the store's verifyPatient.
     */
    async runVerify(row, ordinal = null) {
      if (!row || !row.patient || !this.siteID) return;
      const patNum = row.patient.patNum;
      if (patNum === null || patNum === undefined) return;
      // Guard against a double-click while a check is already in flight.
      const current = this.verifyByPat[patNum];
      if (current && current.loading) return;

      const appt = row.appointment || {};
      this.$set(this.verifyByPat, patNum, { loading: true, message: '', type: 'info' });

      try {
        const data = await this.verifyPatient({
          siteID: this.siteID,
          patNum,
          provNum: appt.provNum,
          aptNum: appt.aptNum,
          aptDateTime: appt.aptDateTime,
          triggerType: 'on_demand',
          // null → omitted by the store → backend checks every plan (2 billed
          // checks on a dual-plan patient). 1|2 → that plan only, 1 check.
          ordinal,
        });

        // A pre-flight bailout (200, ok:false, a known reason) → fix-it message.
        // `blocked_bad_data` names the offending field(s), so it needs the
        // backend's `blocks`. The /verify handler does not currently echo
        // runEligibilityCheck's `blocks` array, so fall back to the row's own
        // preflight issue — the dashboard screens the SAME member ID via the
        // shared screenMemberId. With neither, verifyReasonMessage degrades to a
        // generic (still zero-cost-explicit) message rather than going silent.
        const serverBlocks = (data && Array.isArray(data.blocks)) ? data.blocks : [];
        const rowIssue = rowDataIssue(row);
        const blocks = serverBlocks.length
          ? serverBlocks
          : (rowIssue ? [rowIssue] : []);
        const reasonMsg = data && data.reason
          ? verifyReasonMessage(data.reason, { blocks })
          : null;
        if (reasonMsg) {
          // Carry the RAW reason, not just the rendered copy. The drawer needs to know
          // whether this refusal is terminal (payer does no 270/271 at all) or
          // fix-and-retry, so it can drop the "Verify now" call-to-action in the first
          // case. Deriving that by string-matching the message would break the moment
          // anyone reworded it.
          this.$set(this.verifyByPat, patNum, {
            loading: false,
            message: reasonMsg,
            type: 'warning',
            reason: (data && data.reason) || null,
          });
          if (this.$toast) this.$toast.warning(reasonMsg);
          return;
        }

        // A real, persisted result → refresh the dashboard window so the row's status
        // chip updates in place. This deliberately does NOT open the drawer: the table's
        // Verify is a one-click action and yanking a panel open over the list after it
        // finishes would steal focus from whatever the user does next. The drawer is
        // updated ONLY when it is already open on this patient (`stillOnPatient` below).
        this.$set(this.verifyByPat, patNum, { loading: false, message: '', type: 'info' });
        // When the patient carries a secondary plan the backend checks it too, which means
        // TWO billed checks. Say so in the toast: the office should never have to infer
        // from the invoice that a click cost double. A SCOPED check ran one plan only, so
        // it must not claim two — `data.secondary` is null in that case by construction
        // (the backend skips the secondary pass whenever an ordinal was supplied).
        const sec = data && data.secondary;
        if (this.$toast) {
          if (sec) this.$toast.success('Both plans verified (2 checks — primary and secondary)');
          else if (ordinal === 2) this.$toast.success('Secondary plan verified (1 check)');
          else this.$toast.success('Verification updated');
        }

        // The rows this check actually wrote. /verify echoes them (added 2026-08-17); an
        // older backend omits them, in which case these are null and the drawer falls back
        // to the refreshed row's ids exactly as before.
        const verifiedIds = {
          primary: (data && data.verificationId) || null,
          secondary: (sec && sec.verificationId) || null,
        };

        // ⛔ REBIND THE ROW BEFORE RE-FETCHING DETAIL — this ordering is the fix for a
        // real wrong-plan bug, not tidiness.
        //
        // `loadDetail(..., {latest:true})` drops the row's verification id and asks the
        // backend for the patient's latest check. That resolves via
        // latestVerificationForPatientSql: `IsLatest = 1 ... ORDER BY CheckedAt DESC`.
        // But IsLatest is per (SiteID, PatNum, PlanNum), so a dual-plan patient has TWO
        // IsLatest rows — and the secondary is checked immediately AFTER the primary in
        // the same /verify call, so it always wins that ORDER BY. The drawer would then
        // render the SECONDARY plan's benefits under the primary view: not an empty
        // panel, but the wrong coverage, on exactly the patients whose coordination of
        // benefits decides what they owe.
        //
        // /verify returns no verification id, so we recover the new ids from the
        // refreshed dashboard row and fetch each plan by EXACT id instead.
        // ⛔ Refresh the list the row actually CAME FROM. In search mode the dashboard does
        // not contain this patient — that is the whole reason they were searched for — so
        // reloading it would leave `fresh` undefined, the drawer bound to a stale row, and
        // the new verification ids unreachable. Re-running the search refetches the same
        // rows through the same assembler and yields the ids the rebind needs.
        if (this.searchMode) {
          await this.runPatientSearch();
        } else {
          await this.loadDashboard();
        }
        const refreshedRows = this.searchMode ? this.patientSearch.rows : this.dashboard.rows;
        const stillOnPatient = this.drawer.open && this.drawer.row
          && this.drawer.row.patient && this.drawer.row.patient.patNum === patNum;
        if (stillOnPatient) {
          const fresh = refreshedRows.find(
            (r) => r && r.patient && r.patient.patNum === patNum
          );
          if (fresh) this.drawer.row = fresh;
          // The ids the check itself returned. The rebind above still runs — the TABLE row
          // needs its fresh chips and benefits — but the drawer no longer depends on it
          // having found anything: if the refresh did not return this patient, `fresh` is
          // undefined and the row still carries its PRE-verify id, which would re-fetch the
          // old check and present it as the new result.
          this.reloadPlanDetail(ordinal, verifiedIds);
        }
      } catch (err) {
        const cls = classifyVerifyError(err);
        this.$set(this.verifyByPat, patNum, { loading: false, message: cls.message, type: cls.type });
        if (this.$toast && typeof this.$toast[cls.type] === 'function') {
          this.$toast[cls.type](cls.message);
        }
      }
    },
  },

  beforeDestroy() {
    this.resetDetail();
  },
};
</script>

<style lang="scss" scoped>
.iv-page {
  // Full-width like Reviews.vue's .reviews-page — centering with margin:auto
  // creates a huge left gap off the sidebar on wide monitors.
  max-width: 100%;
  padding: 14px 8px 90px;
  color: #0c0f0e;
}

.iv-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.iv-header-main {
  min-width: 0;
}

.iv-settings-btn {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 16px;
  margin-top: 4px;
  border: 1px solid #c8cfcd;
  background: #ffffff;
  color: #0b5c49;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover { border-color: #0b5c49; background: #e9f6f1; }

  .fa { font-size: 14px; }
}

.iv-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #0f7259;
  margin-bottom: 8px;
}

.iv-title {
  margin: 0;
  font-weight: 800;
  font-size: 34px;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: #0c0f0e;
}

.iv-subtitle {
  margin: 8px 0 0;
  font-size: 15px;
  color: #717977;
  max-width: 640px;
  line-height: 1.5;
}

.iv-notice {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 22px;
  background: #ffffff;
  border: 1px solid #dfe4e3;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.05);

  .fa {
    font-size: 22px;
    color: #9ca4a2;
  }
}

.iv-notice-title {
  font-weight: 700;
  font-size: 15px;
  color: #262b2a;
}

.iv-notice-sub {
  font-size: 13px;
  color: #717977;
  margin-top: 2px;
}

.iv-error {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #fdeeee;
  border: 1px solid #f8d4d5;
  border-radius: 12px;
  font-size: 13.5px;
  color: #a5292e;
}

.iv-error-retry {
  margin-left: auto;
  border: 1px solid #e5484d;
  background: #ffffff;
  color: #a5292e;
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #fdeeee;
  }
}

.iv-table-card {
  background: #ffffff;
  border: 1px solid #dfe4e3;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.05);
  overflow: hidden;
}

// Search mode banner. Blue rather than the page's teal: this is an informational mode
// change, not a status, and teal here would read as "verified".
.iv-searchmode {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 0 12px;
  padding: 11px 16px;
  background: #eff5ff;
  border: 1px solid #c7dbfb;
  border-radius: 10px;
  font-size: 13px;
  color: #1e40af;

  &--error {
    background: #fdeeee;
    border-color: #f5c2c3;
    color: #a12a2e;
  }
}

.iv-searchmode-text {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

.iv-searchmode-more {
  color: #b45309;
  font-weight: 600;
}

.iv-searchmode-back {
  flex: none;
  border: 1px solid #c7dbfb;
  background: #ffffff;
  color: #1e40af;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: #dbe9fe; }
}

// The escape hatch, shown only when the in-window filter found nobody.
.iv-searchall {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  padding: 14px 18px;
  font-size: 13px;
  color: #717977;
  border-top: 1px solid #eef1f0;
}

.iv-searchall-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #0f7259;
  background: #ffffff;
  color: #0f7259;
  border-radius: 8px;
  padding: 6px 13px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  &:not(:disabled):hover { background: #e9f6f1; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.iv-table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 18px;
  font-size: 13px;
  color: #717977;

  strong {
    color: #0c0f0e;
  }
}
</style>
